import * as React from "react";
import { DataGrid, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Close } from "@mui/icons-material";
import { axiosInstance } from "../../../utils/tokenSetCookie";
import { EditClassLocationList } from "../../../types/types";
import { toast } from "react-toastify";
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const columns: GridColDef[] = [
  { field: "locationName", headerName: "Location name", flex: 1 },
  { field: "businessStreet1", headerName: "Street Address", flex: 1 },
  { field: "businessCity", headerName: "City", flex: 1 },
  { field: "businessPhone", headerName: "Phone Number", flex: 1 },
];


const filterOptions = [
  { value: "All", label: "All" },
  { value: "Assigned Locations", label: "Assigned Locations" },
  { value: "Not Assigned Locations", label: "Not Assigned Locations" },
];

interface AssignLocationDialogProps {
  open: boolean;
  onClose: () => void;
  editId: { id: number; className: string };
}

const AssignLocation: React.FC<AssignLocationDialogProps> = ({
  open,
  onClose,
  editId,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredRows, setFilteredRows] = useState<EditClassLocationList[]>([]);
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    const checkedRows = filteredRows
      .filter((row) => row.checked)
      .map((row) => row.id);
    setSelectedRows(checkedRows);
  }, [filteredRows]);

  useEffect(() => {
    if (filteredRows.length > 0) {
      setTimeout(() => {
        setSelectedRows(
          filteredRows.filter((row) => row.checked).map((row) => row.id)
        );
      }, 0); // Allows state update after re-render
    }
  }, [filteredRows]);

  useEffect(() => {
    const getLocationClassDetail = async () => {
      if (!editId?.id) return; // Prevent unnecessary API calls
      try {
        const response = await axiosInstance.get(
          API_BASE_URL +
            `location/locationByClass?businessEntityId=1&classId=${editId.id}`
        );
        if (response.status >= 200 && response.status <= 300) {
          setFilteredRows(response.data.data);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        toast.error("Failed to fetch locations");
      }
    };
    getLocationClassDetail(); // Passing the payload as an argument
  }, [editId]);


  // Handle checkbox selection changes
  const handleSelectionChange = (selectionModel: GridRowSelectionModel) => {
    setSelectedRows(selectionModel);

    // Ensure immutability when updating state
    setFilteredRows((prevRows) =>
      prevRows.map((row) => ({
        ...row,
        checked: selectionModel.includes(row.id),
      }))
    );
  };

  // Filter rows based on search query

  const handleSave = async () => {
    setLoading(true);
    const payload = {
      classId: editId.id,
      locationId: selectedRows.join(","),
    };

    try {
      const response = await axiosInstance.post(
        API_BASE_URL + "location/assignLocationToClass",
        payload
      );
      if (response.status >= 200 && response.status <= 300) {
        toast.success(response.data.data, { autoClose: 1000 });
        onClose();
      }
    } catch (err) {
      console.error("Error saving data:", err);
      toast.error("Failed to assign locations");
    } finally {
      setLoading(false);
    }
  };
  const filteredData = filteredRows.filter((row) =>
    row.locationName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box sx={{ margin: "20px" }}>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md" // Limits width to medium size for large screens
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: "#f9f9f9",
            borderRadius: "12px",
            width: "90%", // Responsive width
            maxWidth: "800px", // Max width on large screens
            minWidth: "300px", // Ensures usability on small screens
          },
        }}
      >
        <DialogTitle className="dialogtitle" sx={{ marginBottom: "6px" }}>
          <Typography variant="h6">{t("manage_locations")}</Typography>
          <IconButton
            color="inherit"
            onClick={onClose}
            aria-label="close"
            sx={{
              position: "absolute",
              right: "16px",
              top: "12px",
              border: "1px solid #be1e2d", // Red border
              color: "#be1e2d", // Default text color
              "&:hover": {
                backgroundColor: "#be1e2d", // Full red background on hover
                color: "#ffffff", // White icon color
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            maxHeight: "80vh",
            overflowY: "auto",
            padding: { xs: 2, sm: 3 },
          }}
        >
          <Grid
            container
            spacing={2}
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {/* Location Class Name */}
            <Grid item xs={12} sm={6}>
              <FormLabel className="addUser_formlabel">
                {t("location_class_name")}
              </FormLabel>
              <TextField
                label={editId.className}
                size="small"
                variant="outlined"
                fullWidth
                sx={{ backgroundColor: "#f0f2f4", cursor: "not-allowed" }}
                InputProps={{
                  readOnly: true,
                  style: { pointerEvents: "none" },
                }}
              />
            </Grid>

            {/* Location Name */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel className="addUser_formlabel">
                  {t("location_name")}
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </FormControl>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <FormLabel className="addUser_formlabel">{t("filter")}</FormLabel>
              <Select
                defaultValue="All"
                disableUnderline
                size="small"
                sx={{
                  border: "1px solid lightgrey",
                  borderRadius: "4px",
                  "&.Mui-focused": { border: "1px solid lightgrey" },
                  "& .MuiInputBase-input": { color: "black" },
                }}
              >
                {filterOptions.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Responsive DataGrid */}
          <Box sx={{ mt: 2, width: "100%", overflowX: "auto" }}>
            <DataGrid
              rows={filteredData}
              columns={columns}
              pageSizeOptions={[5, 10]}
              checkboxSelection
              onRowSelectionModelChange={handleSelectionChange}
              rowSelectionModel={selectedRows}
              sx={{
                border: 0,
                "& .MuiDataGrid-root": {
                  width: "100%",
                  minWidth: "500px", // Ensures usability on small screens
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "flex-end", padding: "16px" }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              padding: "8px 16px",
              height: "40px",
              textTransform: "none",
              marginRight: "8px",
              color: "#ffffff",
              background: "#be1e2d",
              borderColor: "#be1e2d", // Make the border match the text color
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={loading}
            sx={{
              padding: "8px 16px",
              height: "40px",
              textTransform: "none",
            }}
          >
            {/* {t("save")} */}
            {loading ? t("saving...") : t("save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssignLocation;

